// /remittance — SEO-optimized Korean expat money transfer comparison page
// Organic search target: "best way to send money to Korea", "USD to KRW transfer fees"
// Revenue: Wise (partners.wise.com) + Remitly (impact.com) affiliate commissions

import { Link } from 'react-router-dom';
import { ArrowLeft, DollarSign, ShieldCheck, Zap, HelpCircle, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import RemittanceWidget from '@/components/remittance/RemittanceWidget';
import SEO from '@/components/common/SEO';

// ---------------------------------------------------------------------------
// FAQ section data — structured for SEO (targets long-tail search queries)
// ---------------------------------------------------------------------------
const FAQS = [
  {
    q: 'What is the cheapest way to send money from the US to Korea?',
    a: 'There is no single cheapest provider for every amount or funding method. Compare the KRW the recipient will receive after both the visible fee and the exchange-rate margin, then confirm the final quote on the provider’s site before sending.',
  },
  {
    q: 'How long does it take to send money to Korea?',
    a: 'Delivery time depends on the provider, funding method, identity checks, Korean banking hours, and the recipient bank. Treat the estimate shown here as a comparison aid and use the delivery promise in the provider’s final quote.',
  },
  {
    q: 'What is today\'s USD to KRW exchange rate?',
    a: 'The widget shows the latest available market rate and labels cached data when the live source is unavailable. The rate offered to you can differ because providers apply their own fees and exchange-rate margins.',
  },
  {
    q: 'Is Wise safe to use for sending money to Korea?',
    a: 'Wise operates through regulated entities in the markets it serves, but availability and protections depend on your location. Check the provider’s current US regulatory disclosures and terms before opening an account or sending money.',
  },
  {
    q: 'Can I send Korean won (KRW) from the US?',
    a: 'Most services only allow you to send USD and have it converted to KRW on arrival. You enter the USD amount you want to send and the recipient receives KRW in their Korean bank account.',
  },
  {
    q: 'What is a rate alert?',
    a: 'A rate alert notifies you by email when the USD/KRW exchange rate hits your target — for example, "notify me when the rate goes above ₩1,380." This is useful if you want to time your transfer when the rate is favorable. Set one using the bell icon on any provider card.',
  },
];

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-gray-200 dark:border-gray-700 last:border-0">
      <button
        className="w-full flex items-center justify-between gap-3 py-4 text-left text-sm font-medium text-gray-900 dark:text-white hover:text-[#0047AB] dark:hover:text-blue-400 transition-colors"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span>{q}</span>
        <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <p className="pb-4 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{a}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function RemittancePage() {
  return (
    <div className="max-w-2xl mx-auto px-4 py-6 pb-24">
      <SEO
        title="Send Money to Korea — USD to KRW Rate Comparison"
        description="Compare the latest available USD to KRW rate and fee estimates from supported providers, then verify the final quote before sending."
        path="/remittance"
      />
      {/* Back nav */}
      <Link
        to="/guides/sending-money-to-korea"
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to the money guide
      </Link>

      {/* Hero */}
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Send Money to Korea
        </h1>
        <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
          Compare the latest available USD→KRW quotes from supported providers.
          Enter an amount to estimate fees and recipient totals, then verify the final quote before sending.
        </p>
      </div>

      {/* Trust badges */}
      <div className="grid grid-cols-3 gap-3 mb-8">
        {[
          { icon: <DollarSign className="w-4 h-4" />, label: 'Current rates', sub: 'Cached data is labelled' },
          { icon: <ShieldCheck className="w-4 h-4" />, label: 'Transparent', sub: 'Estimates are marked' },
          { icon: <Zap className="w-4 h-4" />, label: 'Rate alerts', sub: 'Email when rate hits target' },
        ].map((b) => (
          <div key={b.label} className="flex flex-col items-center text-center p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
            <span className="text-[#0047AB] dark:text-blue-400 mb-1">{b.icon}</span>
            <span className="text-xs font-semibold text-gray-900 dark:text-white">{b.label}</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">{b.sub}</span>
          </div>
        ))}
      </div>

      {/* Main widget */}
      <RemittanceWidget />

      {/* FAQ section */}
      <div className="mt-12">
        <div className="flex items-center gap-2 mb-4">
          <HelpCircle className="w-4 h-4 text-[#0047AB]" />
          <h2 className="font-semibold text-gray-900 dark:text-white">Frequently asked questions</h2>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 px-4">
          {FAQS.map((faq) => (
            <FAQItem key={faq.q} q={faq.q} a={faq.a} />
          ))}
        </div>
      </div>

      {/* Structured data for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: FAQS.map((faq) => ({
              '@type': 'Question',
              name: faq.q,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.a,
              },
            })),
          }),
        }}
      />
      <p className="mt-8 text-center text-xs text-gray-400 dark:text-gray-600 px-4">
        Some links on this page are affiliate links. KoreanHub may earn a commission if you sign up through them — at no extra cost to you. Rates and fees are sourced from provider APIs and updated regularly; always confirm final amounts on the provider's site before transferring.
      </p>
    </div>
  );
}
